import { createBrowserRouter, RouterProvider, Navigate, redirect } from "react-router-dom";
import RootLayout from "./RootLayout";
import ErrorPage from "./error-page";
import { GameLedger } from "./pages/ledger";

import { AuthProvider } from "./contexts/AuthContext"
import { TermsProvider } from "./contexts/TermsContext"
import { ImpersonationProvider } from "./contexts/ImpersonationContext"
import OGCard from "./pages/og/OGCard";
import { House } from "./pages/house";
import Members from "./pages/member/Members";
import PlayerDetail from "./pages/member/PlayerDetail";
import { protectedLoader, redirectOnAuthenticatedLoader } from "./utilities/authUtils";
import { LoginPage } from "./pages/auth/LoginPage";
import { LogoutPage } from "./pages/auth/LogoutPage";
import { ForgotPasswordPage } from "./pages/auth/ForgotPasswordPage";
import { ResetPasswordPage } from "./pages/auth/ResetPasswordPage";
import { VerifyEmailPage } from "./pages/auth/VerifyEmailPage";
import { RegisterPage } from "./pages/signup/SignUpPage";
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminStats from './pages/admin/AdminStats';
import AdminHouses from './pages/admin/AdminHouses';
import EmailCampaign from './pages/admin/EmailCampaign';
import SurveyResults from './pages/admin/SurveyResults';
import Survey from './pages/survey/Survey';
import Favicon from './components/Favicon';
import GameDetails from './pages/admin/GameDetails';
import NewAccounts from './pages/admin/NewAccounts';
import ActiveHosts from './pages/admin/ActiveHosts';
import FeedbackPage from "./pages/feedback/FeedbackPage";
import ProfilePage from "./pages/profile/ProfilePage";
import HouseProvider from "./contexts/HouseContext";

const router = createBrowserRouter([
    {
        id: "root",
        path: "/",
        Component: RootLayout,
        ErrorBoundary: ErrorPage,
        children: [
            {
                index: true,
                element: <Navigate to="/login" replace />,
            },
            {
                path: "sign-up",
                action: registerAction,
                loader: redirectOnAuthenticatedLoader,
                Component: RegisterPage,
            },
            {
                path: "login",
                loader: redirectOnAuthenticatedLoader,
                Component: LoginPage,
            },
            {
                // Public: logged-out forgot-password request page.
                path: "forgot-password",
                Component: ForgotPasswordPage,
            },
            {
                // Public: set a new password from the emailed reset link
                // (/reset-password?token=…). No auth — the token is the credential.
                path: "reset-password",
                Component: ResetPasswordPage,
            },
            {
                // Public: confirm email from the emailed verification link
                // (/verify-email?token=…). No auth — the token is the credential.
                path: "verify-email",
                Component: VerifyEmailPage,
            },
            {
                path: "logout",
                Component: LogoutPage,
            },
            {
                // Public: campaign survey landing. Recipients click through from
                // an email and are usually not logged in (no protectedLoader).
                // slug is a friendly name (your-games, comeback).
                path: "survey/:slug",
                Component: Survey,
            },
            {
                id: "house",
                path: "games",
                element: <HouseProvider><House /></HouseProvider>,
                loader: protectedLoader()
            },
            {
                path: "players",
                element: <HouseProvider><Members/></HouseProvider>,
                loader: protectedLoader(),
            },
            {
                path: "players/:memberId",
                element: <HouseProvider><PlayerDetail/></HouseProvider>,
                loader: protectedLoader(),
            },
            {
                // Renamed Members -> Players; keep the old path redirecting.
                path: "members",
                element: <Navigate to="/players" replace />,
            },
            {
                path: "games/:gameId",
                element: <HouseProvider><GameLedger/></HouseProvider>,
                loader: protectedLoader(),
            },
            {
                path: "/feedback",
                element: <HouseProvider><FeedbackPage/></HouseProvider>,
                loader: protectedLoader(),
            },
            {
                path: "/profile",
                Component: ProfilePage,
                loader: protectedLoader(),
            },
            {
                path: "/admin",
                Component: AdminDashboard,
                loader: protectedLoader(),
            },
            {
                path: "/admin/stats",
                Component: AdminStats,
                loader: protectedLoader(),
            },
            {
                path: "/admin/stats/created-games",
                Component: GameDetails,
                loader: protectedLoader(),
            },
            {
                // Renamed to decouple the URL from a fixed time range; keep
                // the old path redirecting.
                path: "/admin/stats/last-month",
                element: <Navigate to="/admin/stats/created-games" replace />,
            },
            {
                path: "/admin/stats/new-accounts",
                Component: NewAccounts,
                loader: protectedLoader(),
            },
            {
                path: "/admin/stats/active-hosts",
                Component: ActiveHosts,
                loader: protectedLoader(),
            },
            {
                path: "/og",
                element: <OGCard/>,
            },
            {
                path: "/admin/houses",
                Component: AdminHouses,
                loader: protectedLoader(),
            },
            {
                path: "/admin/email",
                Component: EmailCampaign,
                loader: protectedLoader(),
            },
            {
                path: "/admin/surveys",
                Component: SurveyResults,
                loader: protectedLoader(),
            },
            {
                // Catch-all. In split mode the app has no marketing 404, so
                // unrecognised paths bounce to /login; otherwise the editorial
                // 404 renders on the marketing surface.
                path: "*",
                element: <Navigate to="/login" replace />,
            },
        ]
    },
]);

async function registerAction({ request }) {
    const formData = await request.formData();
    const { email, password, firstName, lastName, description, redirectTo } = Object.fromEntries(formData);
    const acceptedTerms = formData.get("acceptedTerms") === "on";

    if (!email || !password || !description) {
        return {
            error: "You must provide username and password and description to register"
        }
    }

    if (!acceptedTerms) {
        return {
            error: "You must agree to the Terms of Service and Privacy Policy to register"
        }
    }

    try {
        const response = await fetch("/register", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                email,
                password,
                firstName,
                lastName,
                description,
                acceptedTerms,
                // Injected by the Turnstile widget when configured; empty
                // otherwise (and the backend only enforces it when its secret
                // is configured).
                turnstileToken: formData.get("cf-turnstile-response") || "",
            }),
        });

        if (response.status === 409) {
            return { error: `A user already exists with email: ${email}` };
        }
        if (!response.ok) {
            return { error: "Registration failed" };
        }
        // Registration sets the auth cookie server-side. A client-side redirect
        // is enough now that RootLayout re-verifies auth on navigation — the
        // jump into the app refreshes AuthContext, so the House loads without a
        // full page reload.
        const dest = redirectTo && redirectTo.startsWith("/") && redirectTo !== "/" ? redirectTo : "/games";
        return redirect(dest);
    } catch (err) {
        console.log("register failed...", err);
        return { error: "Registration failed" };
    }
}

export default function App() {
    return (
        <AuthProvider>
            <TermsProvider>
                <ImpersonationProvider>
                    <Favicon />
                    <RouterProvider
                        router={router}
                        fallbackElement={<p>Initial Load...</p>}
                    />
                </ImpersonationProvider>
            </TermsProvider>
        </AuthProvider>
    );
}
